import flagsmith from 'flagsmith';
import { isArray } from 'lodash';

/**
 * Wrapper for the Flagsmith hasFeature function. This function will only work after
 * the Flagsmith has initialised. As our FlagsContext prevents children from rendering
 * and should be wrapping your app, you should be fine to use this function anywhere.
 */
/**
 * Local development escape hatch: a comma-separated list of flag keys to treat
 * as enabled without touching the Flagsmith DEV environment. Useful when
 * reviewing or developing a flag-gated feature whose flag does not exist yet.
 *
 *   # shell/.env.local (gitignored), or inline:
 *   PUBLIC_FORCE_FEATURE_FLAGS=farm_areas,uk_book_viewing_wizard yarn start:shell
 *
 * The `PUBLIC_` prefix is what makes the value reach the browser: the Rsbuild
 * CLI loads `.env*` files and merges every `PUBLIC_*` var into
 * `source.define` before our own config is applied.
 *
 * It can only force a flag ON, never off, and it is compiled out of production
 * builds — `process.env.NODE_ENV` is a literal via `shared/rsbuild.config.ts`,
 * so the whole list collapses to `[]` and the env var is never read. Note that
 * this also makes the override inert under `yarn start-react-prod`, which runs
 * shell with `NODE_ENV=production`.
 */
const FORCED_FLAGS: string[] =
  process.env.NODE_ENV === 'production'
    ? []
    : (process.env.PUBLIC_FORCE_FEATURE_FLAGS ?? '')
        .split(',')
        .map((flag) => flag.trim())
        .filter(Boolean);

export function hasFeatureFlags(flagsToFind: string | string[]) {
  const flagsAsArray = isArray(flagsToFind) ? flagsToFind : [flagsToFind];
  return flagsAsArray.reduce((hasFlags, flag) => {
    if (FORCED_FLAGS.includes(flag)) {
      return hasFlags;
    }
    return hasFlags && flagsmith.hasFeature(flag);
  }, true);
}
